import { useState, useEffect } from "react";
import { Form, Input, Upload, Button, Card, Space, Image } from "antd";
import { UploadOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useCreateComplianceMutation } from "./compliance.service";
import { toast } from "react-toastify";

const { TextArea } = Input;

export default function ComplianceCreatePage() {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const navigate = useNavigate();

  const [createComplianceRequest, { isSuccess }] =
    useCreateComplianceMutation();

  useEffect(() => {
    if (isSuccess) {
      form.resetFields();
      navigate("/compliance");
    }
  }, [isSuccess]);

  const uploadProps = {
    name: "image",
    listType: "picture-card",
    fileList: fileList,
    maxCount: 1,
    accept: "image/*",
    beforeUpload: (file) => {
      // Validate file type
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        toast.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }

      // Validate file size (5MB)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        toast.error("Image must be smaller than 5MB!");
        return Upload.LIST_IGNORE;
      }

      return false; // Prevent auto upload
    },
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  // Handle form submission
  const onFinish = async (values) => {
    // Validate image is uploaded
    if (fileList.length === 0 || !fileList[0].originFileObj) {
      toast.error("Please select an image");
      setLoading(false);
      return;
    }

    const formData = new FormData();

    formData.append("title", values.title);
    formData.append("image", fileList[0].originFileObj);

    await createComplianceRequest(formData);
  };

  // Upload button
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload Image</div>
    </div>
  );

  return (
    <Card
      title="Compliance Create"
      style={{ fontSize: "20px" }}
      bordered={false}
      extra={
        <Button
          type="default"
          style={{ marginRight: 8 }}
          onClick={() => navigate("/compliance")}
        >
          Discard
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          title: "",
        }}
      >
        {/* Image Upload Section */}
        <Form.Item
          label={
            <Space>
              <UploadOutlined />
              <span>Compliance Image</span>
            </Space>
          }
          required
          extra="Supported formats: JPEG, PNG, GIF, WebP, SVG. Maximum size: 50MB"
        >
          {fileList.length === 0 ? (
            <Upload {...uploadProps}>{uploadButton}</Upload>
          ) : (
            <div>
              <div
                style={{
                  marginBottom: 12,
                  color: "#52c41a",
                  fontWeight: "bold",
                }}
              >
                ✓ Image selected: {fileList[0].name}
              </div>
              <Image
                width={200}
                height={150}
                src={
                  fileList[0].originFileObj
                    ? URL.createObjectURL(fileList[0].originFileObj)
                    : fileList[0].url
                }
                alt="Uploaded compliance image"
                style={{
                  objectFit: "cover",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                }}
                placeholder={
                  <div
                    style={{
                      width: 200,
                      height: 150,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f5f5f5",
                    }}
                  >
                    Loading...
                  </div>
                }
              />
              <div style={{ marginTop: 8 }}>
                <Button size="small" onClick={() => setFileList([])} danger>
                  Remove Image
                </Button>
              </div>
            </div>
          )}
        </Form.Item>

        {/* Title Section */}
        <Form.Item
          label={
            <Space>
              <span>Compliance Title</span>
              <span style={{ color: "#ff4d4f" }}>*</span>
            </Space>
          }
          name={"title"}
          rules={[
            {
              required: true,
              message: "Title is required",
            },
            {
              max: 200,
              message: "Title cannot exceed 200 characters",
            },
          ]}
          required
        >
          <TextArea
            rows={3}
            maxLength={200}
            showCount
            style={{ resize: "none" }}
          />
        </Form.Item>
        <Form.Item style={{ marginTop: 32 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<SaveOutlined />}
            size="large"
            block
            style={{ height: "48px", fontSize: "16px" }}
          >
            {loading ? "Creating..." : "Create Compliance"}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}
